import { Button } from "antd";
import {
  SafetyCertificateOutlined,
  WarningFilled,
  ClockCircleFilled,
  CloseCircleFilled,
} from "@ant-design/icons";
import { useNavigate } from "react-router";
import { ROUTES } from "../../constants/routes.constant";

import { UserData } from "../../models/user";

interface PendingKycProps {
  user: UserData;
}
const PendingKyc = ({ user }: PendingKycProps) => {
  const navigate = useNavigate();

  const handleVerify = () => {
    navigate(ROUTES.KYC_VERIFICATION.path);
  };

  const pendingKyc = user?.pendingKycRequest;

  // Determine correct admin remark based on rejection
  let adminRemark = pendingKyc?.admin_remark || "";

  if (pendingKyc?.status === "approved") {
    return null;
  } // Dynamic UI Config
  const uiConfig =
    pendingKyc?.status === "rejected"
      ? {
          bg: "bg-red-50",
          border: "border-red-300",
          titleColor: "text-red-700",
          descColor: "text-red-600",
          title: "KYC Verification Rejected",
          description:
            adminRemark ||
            "Your KYC verification was rejected. Please review and resubmit your documents.",
          icon: (
            <CloseCircleFilled
              style={{
                fontSize: "22px",
                color: "#dc2626",
                marginRight: "6px",
              }}
            />
          ),
          buttonText: "Re-submit KYC",
          buttonDanger: true,
        }
      : pendingKyc?.status === "pending"
        ? {
            bg: "bg-yellow-50",
            border: "border-yellow-300",
            titleColor: "text-yellow-700",
            descColor: "text-yellow-600",
            title: "KYC Verification In Review",
            description:
              "Your KYC documents are under review. Verification may take some time.",
            icon: (
              <ClockCircleFilled
                style={{
                  fontSize: "22px",
                  color: "#ca8a04",
                  marginRight: "6px",
                }}
              />
            ),
            buttonText: "",
            buttonDanger: false,
          }
        : {
            bg: "bg-orange-50",
            border: "border-orange-300",
            titleColor: "text-orange-700",
            descColor: "text-orange-600",
            title: "KYC Verification Pending",
            description:
              "Complete your KYC to start hosting events. Your account is restricted until verified.",
            icon: (
              <WarningFilled
                style={{
                  fontSize: "22px",
                  color: "#ea580c",
                  marginRight: "6px",
                }}
              />
            ),
            buttonText: "Complete KYC",
            buttonDanger: true,
          };

  return (
    <div
      className={`my-6 w-[98%] mx-auto flex items-center justify-between gap-4 p-5 rounded-2xl border shadow-sm ${uiConfig.bg} ${uiConfig.border}`}
    >
      <div className="flex items-center gap-4 w-full">
        <div className="mt-1 flex items-center justify-center">
          {uiConfig.icon}
        </div>

        <div className="flex-1">
          <p
            className={`font-semibold text-sm md:text-base ${uiConfig.titleColor}`}
          >
            {uiConfig.title}
          </p>

          <p
            className={`text-xs md:text-sm mt-1 leading-relaxed ${uiConfig.descColor}`}
          >
            {uiConfig.description}
          </p>
        </div>
      </div>

      {pendingKyc?.status !== "pending" && (
        <Button
          type="primary"
          size="middle"
          danger={uiConfig.buttonDanger}
          icon={<SafetyCertificateOutlined />}
          onClick={handleVerify}
          className="shrink-0"
        >
          {uiConfig.buttonText}
        </Button>
      )}
    </div>
  );
};

export default PendingKyc;
